import { useEffect, useMemo, useState, useCallback } from "react";
import { api, brl, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Plus, Eye, PencilLine, RefreshCw, Search, HandCoins, Ban, AlertTriangle, Link2,
} from "lucide-react";
import { toast } from "sonner";

// Espelha CR_STATUSES e CR_ORIGEM_TIPOS do backend
const STATUSES = [
  { key: "pendente", label: "Pendente" },
  { key: "parcial", label: "Parcial" },
  { key: "recebido", label: "Recebido" },
  { key: "vencido", label: "Vencido" },
  { key: "cancelado", label: "Cancelado" },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.key, s.label]));

const ORIGENS = [
  { key: "avulsa", label: "Avulsa" },
  { key: "reserva", label: "Reserva" },
  { key: "contrato", label: "Contrato" },
  { key: "orcamento_convertido", label: "Orçamento convertido" },
  { key: "outra", label: "Outra" },
];
const ORIGEM_LABEL = Object.fromEntries(ORIGENS.map((o) => [o.key, o.label]));

function today() { return new Date().toISOString().slice(0, 10); }
function firstDayOfMonth() {
  const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function lastDayOfMonth() {
  const d = new Date(); const l = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${l.getFullYear()}-${String(l.getMonth() + 1).padStart(2, "0")}-${String(l.getDate()).padStart(2, "0")}`;
}

const emptyForm = () => ({
  descricao: "",
  valor_original: "",
  desconto: "0",
  acrescimo: "0",
  data_vencimento: today(),
  pousada_id: "",
  plano_de_contas_id: "",
  origem_tipo: "avulsa",
  origem_referencia: "",
  forma_pagamento: "",
  observacoes: "",
  competencia: "",
});

function StatusBadge({ status }) {
  const s = status || "pendente";
  const cls = {
    pendente: "bg-stone-100 text-stone-700 border border-stone-300",
    parcial: "bg-[#FFF4D6] text-[#8B6A1F] border border-[#8B6A1F]/25",
    recebido: "bg-[#EAF0EA] text-[#083A6B] border border-[#083A6B]/20",
    vencido: "bg-[#F5E4DD] text-[#7c3a20] border border-[#D4A652]/25",
    cancelado: "bg-stone-100 text-stone-400 border border-stone-300 line-through",
  }[s] || "bg-stone-100 text-stone-700 border border-stone-300";
  return (
    <span
      data-testid={`cr-status-${s}`}
      className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${cls}`}
    >
      {STATUS_LABEL[s] || s}
    </span>
  );
}

function OrigemTag({ origem_tipo }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs text-stone-700">
      {origem_tipo === "reserva" ? <Link2 size={12} className="text-[#083A6B]" /> : null}
      {ORIGEM_LABEL[origem_tipo] || origem_tipo || "—"}
    </span>
  );
}

export default function ContasReceber() {
  const [items, setItems] = useState([]);
  const [pousadas, setPousadas] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [me, setMe] = useState(null);

  const [loading, setLoading] = useState(true);
  const [erroCarga, setErroCarga] = useState(null);

  const [busca, setBusca] = useState("");
  const [filtros, setFiltros] = useState({
    status: "all",
    origem_tipo: "all",
    pousada_id: "all",
    vencimento_de: firstDayOfMonth(),
    vencimento_ate: lastDayOfMonth(),
    apenas_ativas: true,
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [detalheAlvo, setDetalheAlvo] = useState(null);
  const [detalheOpen, setDetalheOpen] = useState(false);
  const [detalheLoading, setDetalheLoading] = useState(false);

  const [receberAlvo, setReceberAlvo] = useState(null);
  const [receberForm, setReceberForm] = useState({
    valor_recebido: "", data_recebimento: today(), forma_pagamento: "", observacao: "",
  });
  const [recebendo, setRecebendo] = useState(false);

  const [cancelarAlvo, setCancelarAlvo] = useState(null);
  const [cancelarMotivo, setCancelarMotivo] = useState("");
  const [cancelando, setCancelando] = useState(false);

  const podeEscrever = !!(me && (me.is_admin || me.is_finance));

  const load = useCallback(async () => {
    setLoading(true); setErroCarga(null);
    try {
      const params = { limit: 500 };
      if (filtros.status !== "all") params.status = filtros.status;
      if (filtros.origem_tipo !== "all") params.origem_tipo = filtros.origem_tipo;
      if (filtros.pousada_id !== "all") params.pousada_id = filtros.pousada_id;
      if (filtros.vencimento_de) params.vencimento_de = filtros.vencimento_de;
      if (filtros.vencimento_ate) params.vencimento_ate = filtros.vencimento_ate;
      if (filtros.apenas_ativas) params.is_cancelada = false;
      const { data } = await api.get("/contas-receber", { params });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setItems([]);
      setErroCarga(err?.response?.status === 401
        ? "Sessão expirada. Faça login novamente."
        : "Não foi possível carregar as contas a receber.");
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  const loadRefs = useCallback(async () => {
    try {
      const [po, pl, u] = await Promise.all([
        api.get("/pousadas-list").catch(() => ({ data: [] })),
        api.get("/plano-contas", { params: { tree: false } }).catch(() => ({ data: [] })),
        api.get("/auth/me").catch(() => ({ data: null })),
      ]);
      setPousadas(Array.isArray(po.data) ? po.data.filter((x) => x.id) : []);
      // aceitamos tanto lista chapada (tree=false) quanto árvore
      const flat = [];
      const walk = (nodes) => {
        (nodes || []).forEach((n) => {
          if (n.ativo !== false && n.tipo === "receita") flat.push(n);
          if (n.children?.length) walk(n.children);
        });
      };
      if (Array.isArray(pl.data)) {
        // se vier chapado
        if (pl.data.length && !("children" in pl.data[0])) {
          pl.data.forEach((n) => { if (n.ativo !== false && n.tipo === "receita") flat.push(n); });
        } else {
          walk(pl.data);
        }
      }
      setPlanos(flat);
      setMe(u.data);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => { loadRefs(); }, [loadRefs]);
  useEffect(() => { load(); }, [load]);

  const pousadaMap = useMemo(() => Object.fromEntries(pousadas.map((p) => [p.id, p.nome])), [pousadas]);
  const planoMap = useMemo(() => Object.fromEntries(planos.map((p) => [p.id, p])), [planos]);

  const itensFiltrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) => {
      const alvo = [
        c.descricao, c.observacoes, c.forma_pagamento,
        pousadaMap[c.pousada_id],
        planoMap[c.plano_de_contas_id]?.nome,
        c.reserva_id, c.origem_referencia,
      ].filter(Boolean).join(" ").toLowerCase();
      return alvo.includes(q);
    });
  }, [items, busca, pousadaMap, planoMap]);

  const totais = useMemo(() => {
    let total = 0, recebido = 0, saldo = 0, count = 0;
    itensFiltrados.forEach((c) => {
      if (c.is_cancelada) return;
      total += Number(c.valor_final || 0);
      recebido += Number(c.valor_recebido || 0);
      saldo += Number(c.saldo || 0);
      count += 1;
    });
    return { total, recebido, saldo, count };
  }, [itensFiltrados]);

  const abrirNovo = () => {
    setEditingId(null); setForm(emptyForm()); setFormOpen(true);
  };

  const abrirEdicao = (c) => {
    if (c.is_cancelada) return toast.error("Conta cancelada não pode ser editada");
    setEditingId(c.id);
    setForm({
      descricao: c.descricao || "",
      valor_original: String(c.valor_original ?? ""),
      desconto: String(c.desconto ?? "0"),
      acrescimo: String(c.acrescimo ?? "0"),
      data_vencimento: c.data_vencimento || today(),
      pousada_id: c.pousada_id || "",
      plano_de_contas_id: c.plano_de_contas_id || "",
      origem_tipo: c.origem_tipo || "avulsa",
      origem_referencia: c.origem_referencia || "",
      forma_pagamento: c.forma_pagamento || "",
      observacoes: c.observacoes || "",
      competencia: c.competencia || "",
    });
    setFormOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const valorOrig = Number(form.valor_original);
    if (Number.isNaN(valorOrig) || valorOrig < 0) return toast.error("Valor original obrigatório e ≥ 0");
    if (!form.data_vencimento) return toast.error("Data de vencimento obrigatória");

    const desc = Number(form.desconto || 0);
    const acr = Number(form.acrescimo || 0);
    const final = Math.round((valorOrig - desc + acr) * 100) / 100;
    if (final < 0) return toast.error("Valor final não pode ser negativo");

    setSaving(true);
    try {
      if (editingId) {
        // PATCH aceita apenas campos permitidos (nunca reserva_id, origem_*, is_cancelada, valor_recebido_avulsa)
        const payload = {
          descricao: (form.descricao || "").trim(),
          valor_original: valorOrig,
          desconto: desc,
          acrescimo: acr,
          data_vencimento: form.data_vencimento,
          pousada_id: form.pousada_id || null,
          plano_de_contas_id: form.plano_de_contas_id || null,
          forma_pagamento: (form.forma_pagamento || "").trim(),
          observacoes: (form.observacoes || "").trim(),
          competencia: (form.competencia || "").trim() || null,
        };
        // remove nulos undefined para o exclude_unset do backend
        Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
        await api.patch(`/contas-receber/${editingId}`, payload);
        toast.success("Conta a receber atualizada");
      } else {
        // POST — sempre avulsa/manual pela UI (nunca cria reserva)
        const origem_tipo = form.origem_tipo && form.origem_tipo !== "reserva" ? form.origem_tipo : "avulsa";
        const payload = {
          descricao: (form.descricao || "").trim(),
          valor_original: valorOrig,
          desconto: desc,
          acrescimo: acr,
          data_vencimento: form.data_vencimento,
          pousada_id: form.pousada_id || null,
          plano_de_contas_id: form.plano_de_contas_id || null,
          origem_tipo,
          origem_referencia: (form.origem_referencia || "").trim() || null,
          forma_pagamento: (form.forma_pagamento || "").trim(),
          observacoes: (form.observacoes || "").trim(),
          competencia: (form.competencia || "").trim() || null,
        };
        await api.post("/contas-receber", payload);
        toast.success("Conta a receber cadastrada");
      }
      setFormOpen(false); setEditingId(null); setForm(emptyForm());
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao salvar. Verifique os dados.";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const abrirDetalhe = async (c) => {
    setDetalheAlvo(c); setDetalheOpen(true); setDetalheLoading(true);
    try {
      const { data } = await api.get(`/contas-receber/${c.id}`);
      setDetalheAlvo(data);
    } catch { /* mantém básico */ }
    finally { setDetalheLoading(false); }
  };

  const abrirReceber = (c) => {
    if (c.is_cancelada) return toast.error("Conta cancelada não pode receber");
    if (c.reserva_id) {
      return toast.error(
        "Conta vinculada a reserva: o valor recebido é derivado dos pagamentos da reserva. Utilize o fluxo oficial de pagamento da reserva."
      );
    }
    if (c.status === "recebido") return toast.error("Conta já totalmente recebida");
    setReceberAlvo(c);
    setReceberForm({
      valor_recebido: String(Number(c.saldo || 0)),
      data_recebimento: today(),
      forma_pagamento: c.forma_pagamento || "",
      observacao: "",
    });
  };

  const executarReceber = async () => {
    if (!receberAlvo) return;
    const v = Number(receberForm.valor_recebido);
    if (!v || v <= 0) return toast.error("Valor recebido deve ser > 0");
    if (!receberForm.data_recebimento) return toast.error("Informe a data de recebimento");
    setRecebendo(true);
    try {
      await api.post(`/contas-receber/${receberAlvo.id}/receber`, {
        valor_recebido: v,
        data_recebimento: receberForm.data_recebimento,
        forma_pagamento: receberForm.forma_pagamento || "",
        observacao: receberForm.observacao || "",
      });
      toast.success("Recebimento registrado");
      setReceberAlvo(null);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao registrar recebimento";
      toast.error(String(msg));
    } finally {
      setRecebendo(false);
    }
  };

  const abrirCancelar = (c) => {
    if (c.is_cancelada) return toast.error("Conta já cancelada");
    setCancelarAlvo(c); setCancelarMotivo("");
  };

  const executarCancelar = async () => {
    if (!cancelarAlvo) return;
    if (!cancelarMotivo.trim()) return toast.error("Informe o motivo do cancelamento");
    setCancelando(true);
    try {
      await api.post(`/contas-receber/${cancelarAlvo.id}/cancelar`, { motivo: cancelarMotivo.trim() });
      toast.success("Conta a receber cancelada");
      setCancelarAlvo(null); setCancelarMotivo("");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao cancelar conta";
      toast.error(String(msg));
    } finally {
      setCancelando(false);
    }
  };

  return (
    <div data-testid="page-contas-receber">
      <PageHeader
        eyebrow="Financeiro · Contas a Receber"
        title="Contas a Receber"
        description="Compromissos financeiros a receber. Contas vinculadas a reservas têm o valor recebido derivado dos pagamentos oficiais."
        actions={
          podeEscrever ? (
            <Dialog open={formOpen} onOpenChange={(v) => { setFormOpen(v); if (!v) { setEditingId(null); setForm(emptyForm()); } }}>
              <DialogTrigger asChild>
                <Button data-testid="btn-nova-cr" onClick={abrirNovo}>
                  <Plus size={16} /> Nova conta a receber
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>
                    {editingId ? "Editar conta a receber" : "Nova conta a receber (avulsa)"}
                  </DialogTitle>
                  <DialogDescription>
                    {editingId
                      ? "Somente campos permitidos pelo backend são editáveis. Vínculo com reserva e origem não podem ser alterados aqui."
                      : "A UI só cria contas avulsas. Contas vinculadas a reservas são gerenciadas pelos fluxos oficiais de reserva/pagamento."}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="cr-descricao">Descrição</Label>
                    <Input id="cr-descricao" data-testid="input-cr-descricao" value={form.descricao}
                      onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                      placeholder="Ex.: Serviço extra · Diária avulsa" />
                  </div>
                  <div>
                    <Label htmlFor="cr-valor">Valor original (BRL) *</Label>
                    <Input id="cr-valor" data-testid="input-cr-valor" type="number" step="0.01" min="0"
                      value={form.valor_original} required
                      onChange={(e) => setForm({ ...form, valor_original: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="cr-venc">Data de vencimento *</Label>
                    <Input id="cr-venc" data-testid="input-cr-venc" type="date"
                      value={form.data_vencimento} required
                      onChange={(e) => setForm({ ...form, data_vencimento: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="cr-desconto">Desconto (BRL)</Label>
                    <Input id="cr-desconto" data-testid="input-cr-desconto" type="number" step="0.01" min="0"
                      value={form.desconto}
                      onChange={(e) => setForm({ ...form, desconto: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="cr-acrescimo">Acréscimo (BRL)</Label>
                    <Input id="cr-acrescimo" data-testid="input-cr-acrescimo" type="number" step="0.01" min="0"
                      value={form.acrescimo}
                      onChange={(e) => setForm({ ...form, acrescimo: e.target.value })} />
                  </div>
                  {!editingId && (
                    <div>
                      <Label htmlFor="cr-origem-tipo">Origem</Label>
                      <Select value={form.origem_tipo}
                        onValueChange={(v) => setForm({ ...form, origem_tipo: v })}>
                        <SelectTrigger id="cr-origem-tipo" data-testid="select-cr-origem">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ORIGENS.filter((o) => o.key !== "reserva").map((o) => (
                            <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-stone-500 mt-1">
                        A UI não cria conta vinculada a reserva.
                      </p>
                    </div>
                  )}
                  {!editingId && (
                    <div>
                      <Label htmlFor="cr-origem-ref">Referência da origem</Label>
                      <Input id="cr-origem-ref" data-testid="input-cr-origem-ref" value={form.origem_referencia}
                        onChange={(e) => setForm({ ...form, origem_referencia: e.target.value })}
                        placeholder="Nº contrato, ID orçamento, etc." />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="cr-pousada">Pousada</Label>
                    <Select value={form.pousada_id || "none"}
                      onValueChange={(v) => setForm({ ...form, pousada_id: v === "none" ? "" : v })}>
                      <SelectTrigger id="cr-pousada" data-testid="select-cr-pousada">
                        <SelectValue placeholder="Corporativo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem pousada / Corporativo</SelectItem>
                        {pousadas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="cr-plano">Plano de contas (receita)</Label>
                    <Select value={form.plano_de_contas_id || "none"}
                      onValueChange={(v) => setForm({ ...form, plano_de_contas_id: v === "none" ? "" : v })}>
                      <SelectTrigger id="cr-plano" data-testid="select-cr-plano">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Sem plano</SelectItem>
                        {planos.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.codigo} · {p.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="cr-forma">Forma de pagamento</Label>
                    <Input id="cr-forma" data-testid="input-cr-forma" value={form.forma_pagamento}
                      onChange={(e) => setForm({ ...form, forma_pagamento: e.target.value })}
                      placeholder="pix, boleto, transferência..." />
                  </div>
                  <div>
                    <Label htmlFor="cr-competencia">Competência (opcional)</Label>
                    <Input id="cr-competencia" data-testid="input-cr-competencia" value={form.competencia}
                      onChange={(e) => setForm({ ...form, competencia: e.target.value })}
                      placeholder="YYYY-MM ou YYYY-MM-DD" />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="cr-obs">Observações</Label>
                    <Input id="cr-obs" data-testid="input-cr-observacoes" value={form.observacoes}
                      onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
                  </div>
                  <div className="md:col-span-2 text-xs text-stone-600 bg-stone-50 border border-stone-200 rounded px-3 py-2">
                    Valor final calculado: <b className="tabular-nums">
                      {brl(Math.max(0, Number(form.valor_original || 0) - Number(form.desconto || 0) + Number(form.acrescimo || 0)))}
                    </b>
                  </div>
                  <DialogFooter className="md:col-span-2">
                    <Button type="button" variant="outline"
                      onClick={() => { setFormOpen(false); setEditingId(null); setForm(emptyForm()); }}>
                      Cancelar
                    </Button>
                    <Button type="submit" data-testid="btn-salvar-cr" disabled={saving}>
                      {saving ? "Salvando..." : editingId ? "Salvar alterações" : "Cadastrar"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          ) : null
        }
      />

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Vencimento de</Label>
          <Input type="date" data-testid="filter-cr-de" value={filtros.vencimento_de}
            onChange={(e) => setFiltros({ ...filtros, vencimento_de: e.target.value })} className="w-[150px]" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Até</Label>
          <Input type="date" data-testid="filter-cr-ate" value={filtros.vencimento_ate}
            onChange={(e) => setFiltros({ ...filtros, vencimento_ate: e.target.value })} className="w-[150px]" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Status</Label>
          <Select value={filtros.status} onValueChange={(v) => setFiltros({ ...filtros, status: v })}>
            <SelectTrigger data-testid="filter-cr-status" className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Origem</Label>
          <Select value={filtros.origem_tipo} onValueChange={(v) => setFiltros({ ...filtros, origem_tipo: v })}>
            <SelectTrigger data-testid="filter-cr-origem" className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {ORIGENS.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Pousada</Label>
          <Select value={filtros.pousada_id} onValueChange={(v) => setFiltros({ ...filtros, pousada_id: v })}>
            <SelectTrigger data-testid="filter-cr-pousada" className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {pousadas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Escopo</Label>
          <Select value={filtros.apenas_ativas ? "ativas" : "todas"}
            onValueChange={(v) => setFiltros({ ...filtros, apenas_ativas: v === "ativas" })}>
            <SelectTrigger data-testid="filter-cr-escopo" className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ativas">Apenas ativas</SelectItem>
              <SelectItem value="todas">Incluir canceladas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Busca</Label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input value={busca} data-testid="filter-cr-busca"
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Descrição, reserva, observação..." className="pl-8" />
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} aria-label="Atualizar" data-testid="btn-refresh-cr">
          <RefreshCw size={14} />
        </Button>
      </div>

      {/* Totais período */}
      <div className="flex flex-wrap gap-3 mb-4 text-sm">
        <div className="px-3 py-1.5 rounded-md bg-stone-100 text-stone-800 border border-stone-200" data-testid="cr-total-count">
          Contas no período: <b className="tabular-nums">{totais.count}</b>
        </div>
        <div className="px-3 py-1.5 rounded-md bg-[#EAF0EA] text-[#083A6B] border border-[#083A6B]/20" data-testid="cr-total-final">
          Valor total: <b className="tabular-nums">{brl(totais.total)}</b>
        </div>
        <div className="px-3 py-1.5 rounded-md bg-[#EAF0EA] text-[#083A6B] border border-[#083A6B]/20" data-testid="cr-total-recebido">
          Recebido: <b className="tabular-nums">{brl(totais.recebido)}</b>
        </div>
        <div className="px-3 py-1.5 rounded-md bg-[#F5E4DD] text-[#7c3a20] border border-[#D4A652]/25" data-testid="cr-total-saldo">
          Saldo em aberto: <b className="tabular-nums">{brl(totais.saldo)}</b>
        </div>
      </div>

      {/* Corpo */}
      {loading ? (
        <div data-testid="cr-loading" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center text-stone-500">
          Carregando contas a receber...
        </div>
      ) : erroCarga ? (
        <div data-testid="cr-erro" className="bg-[#FFFFFF] border border-[#D4A652]/30 rounded-lg p-10 text-center text-[#D4A652]">
          {erroCarga}
        </div>
      ) : itensFiltrados.length === 0 ? (
        <div data-testid="cr-empty" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-[#EAF0EA] flex items-center justify-center mx-auto mb-4 text-[#083A6B]">
            <HandCoins size={22} strokeWidth={1.5} />
          </div>
          <div className="font-serif text-2xl text-stone-900 mb-2">Nenhuma conta a receber encontrada.</div>
          <p className="text-sm text-stone-600 mb-6 max-w-md mx-auto">
            Ajuste os filtros ou cadastre a primeira conta a receber avulsa. Contas vinculadas a reservas são geradas pelos fluxos oficiais de reserva.
          </p>
          {podeEscrever && (
            <Button data-testid="btn-empty-nova-cr" onClick={abrirNovo}>
              <Plus size={16} /> Nova conta a receber
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile: cards */}
          <div className="md:hidden space-y-3">
            {itensFiltrados.map((c) => (
              <div key={c.id} data-testid={`cr-card-${c.id}`}
                className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="text-xs text-stone-500">Venc. {fmtDate(c.data_vencimento)}</div>
                    <div className="font-serif text-lg text-stone-900 truncate">
                      {c.descricao || <span className="text-stone-400">— sem descrição —</span>}
                    </div>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
                <div className="font-serif text-2xl text-stone-900 tabular-nums mb-1">{brl(c.valor_final)}</div>
                <div className="text-xs text-stone-600 space-y-0.5 mb-3">
                  <div>Recebido: <b className="tabular-nums">{brl(c.valor_recebido)}</b> · Saldo: <b className="tabular-nums">{brl(c.saldo)}</b></div>
                  <div><OrigemTag origem_tipo={c.origem_tipo} /></div>
                  {c.reserva_id && (
                    <div className="font-mono text-[10px] text-stone-500">Reserva: {c.reserva_id.slice(0, 8)}…</div>
                  )}
                  {c.pousada_id && <div>Pousada: {pousadaMap[c.pousada_id] || c.pousada_id}</div>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => abrirDetalhe(c)}
                    data-testid={`btn-cr-detalhe-${c.id}`}><Eye size={14} /> Detalhes</Button>
                  {podeEscrever && !c.is_cancelada && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => abrirEdicao(c)}
                        data-testid={`btn-cr-editar-${c.id}`}><PencilLine size={14} /> Editar</Button>
                      {!c.reserva_id && c.status !== "recebido" && (
                        <Button size="sm" variant="outline" onClick={() => abrirReceber(c)}
                          data-testid={`btn-cr-receber-${c.id}`}><HandCoins size={14} /> Receber</Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => abrirCancelar(c)}
                        data-testid={`btn-cr-cancelar-${c.id}`}><Ban size={14} /> Cancelar</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: tabela */}
          <div className="hidden md:block bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50 hover:bg-stone-50">
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Vencimento</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Descrição</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Origem</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Reserva</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pousada</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Valor</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Recebido</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Saldo</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itensFiltrados.map((c) => {
                  const bloqueadaEdit = c.is_cancelada;
                  const isReserva = !!c.reserva_id;
                  const podeReceber = !bloqueadaEdit && !isReserva && c.status !== "recebido";
                  return (
                    <TableRow key={c.id} data-testid={`cr-row-${c.id}`}>
                      <TableCell className="text-stone-700 text-sm whitespace-nowrap">{fmtDate(c.data_vencimento)}</TableCell>
                      <TableCell className="text-stone-700 text-sm max-w-xs truncate">
                        {c.descricao || <span className="text-stone-400">—</span>}
                      </TableCell>
                      <TableCell><OrigemTag origem_tipo={c.origem_tipo} /></TableCell>
                      <TableCell className="text-stone-700 text-xs">
                        {c.reserva_id
                          ? <span className="font-mono">{c.reserva_id.slice(0, 8)}…</span>
                          : <span className="text-stone-400">—</span>}
                      </TableCell>
                      <TableCell className="text-stone-700 text-sm">
                        {c.pousada_id ? (pousadaMap[c.pousada_id] || c.pousada_id) : <span className="text-stone-400">Corporativo</span>}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-stone-900">{brl(c.valor_final)}</TableCell>
                      <TableCell className="text-right tabular-nums text-[#083A6B]">{brl(c.valor_recebido)}</TableCell>
                      <TableCell className={`text-right tabular-nums ${Number(c.saldo || 0) > 0 ? "text-[#D4A652]" : "text-stone-500"}`}>
                        {brl(c.saldo)}
                      </TableCell>
                      <TableCell><StatusBadge status={c.status} /></TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => abrirDetalhe(c)}
                            aria-label={`Ver detalhes da conta com vencimento ${fmtDate(c.data_vencimento)}`}
                            data-testid={`btn-cr-detalhe-${c.id}`}>
                            <Eye size={14} />
                          </Button>
                          {podeEscrever && !bloqueadaEdit && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => abrirEdicao(c)}
                                aria-label="Editar conta" data-testid={`btn-cr-editar-${c.id}`}>
                                <PencilLine size={14} />
                              </Button>
                              {podeReceber && (
                                <Button size="sm" variant="ghost" onClick={() => abrirReceber(c)}
                                  aria-label="Registrar recebimento" data-testid={`btn-cr-receber-${c.id}`}>
                                  <HandCoins size={14} />
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" onClick={() => abrirCancelar(c)}
                                aria-label="Cancelar conta a receber" data-testid={`btn-cr-cancelar-${c.id}`}>
                                <Ban size={14} />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* Detalhes */}
      <Dialog open={detalheOpen} onOpenChange={(v) => { setDetalheOpen(v); if (!v) setDetalheAlvo(null); }}>
        <DialogContent className="max-w-xl" data-testid="dialog-cr-detalhe">
          <DialogHeader>
            <DialogTitle>Detalhes da conta a receber</DialogTitle>
            <DialogDescription>
              Valores derivados (recebido, saldo, status) são calculados pelo backend conforme regras F3.
            </DialogDescription>
          </DialogHeader>
          {detalheLoading ? (
            <div className="text-center text-stone-500 py-6">Carregando...</div>
          ) : detalheAlvo ? (
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div><div className="text-[11px] uppercase text-stone-500">ID</div><span className="font-mono text-xs break-all">{detalheAlvo.id}</span></div>
              <div><div className="text-[11px] uppercase text-stone-500">Status</div><StatusBadge status={detalheAlvo.status} /></div>
              <div><div className="text-[11px] uppercase text-stone-500">Vencimento</div>{fmtDate(detalheAlvo.data_vencimento)}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Competência</div>{detalheAlvo.competencia || "—"}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Valor original</div><span className="tabular-nums">{brl(detalheAlvo.valor_original)}</span></div>
              <div><div className="text-[11px] uppercase text-stone-500">Desc. / Acréscimo</div>
                <span className="tabular-nums">{brl(detalheAlvo.desconto)} / {brl(detalheAlvo.acrescimo)}</span>
              </div>
              <div><div className="text-[11px] uppercase text-stone-500">Valor final</div><b className="tabular-nums">{brl(detalheAlvo.valor_final)}</b></div>
              <div><div className="text-[11px] uppercase text-stone-500">Recebido</div><span className="tabular-nums text-[#083A6B]">{brl(detalheAlvo.valor_recebido)}</span></div>
              <div><div className="text-[11px] uppercase text-stone-500">Saldo</div><span className="tabular-nums text-[#D4A652]">{brl(detalheAlvo.saldo)}</span></div>
              <div><div className="text-[11px] uppercase text-stone-500">Origem</div><OrigemTag origem_tipo={detalheAlvo.origem_tipo} /></div>
              <div className="col-span-2">
                <div className="text-[11px] uppercase text-stone-500">Reserva vinculada</div>
                {detalheAlvo.reserva_id
                  ? <span className="font-mono text-xs break-all">{detalheAlvo.reserva_id}</span>
                  : <span className="text-stone-400">—</span>}
                {detalheAlvo.reserva_id && (
                  <div className="text-[11px] text-stone-500 mt-1">
                    Valor recebido derivado de <b>pagamentos</b> da reserva. Não editável por esta tela.
                  </div>
                )}
              </div>
              <div><div className="text-[11px] uppercase text-stone-500">Pousada</div>
                {detalheAlvo.pousada_id ? (pousadaMap[detalheAlvo.pousada_id] || detalheAlvo.pousada_id) : "Corporativo"}
              </div>
              <div><div className="text-[11px] uppercase text-stone-500">Plano de contas</div>
                {planoMap[detalheAlvo.plano_de_contas_id]
                  ? `${planoMap[detalheAlvo.plano_de_contas_id].codigo} · ${planoMap[detalheAlvo.plano_de_contas_id].nome}`
                  : "—"}
              </div>
              <div><div className="text-[11px] uppercase text-stone-500">Forma pagamento</div>{detalheAlvo.forma_pagamento || "—"}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Origem ref.</div>{detalheAlvo.origem_referencia || "—"}</div>
              <div className="col-span-2"><div className="text-[11px] uppercase text-stone-500">Descrição</div>{detalheAlvo.descricao || "—"}</div>
              <div className="col-span-2"><div className="text-[11px] uppercase text-stone-500">Observações</div>{detalheAlvo.observacoes || "—"}</div>
              {detalheAlvo.is_cancelada && (
                <div className="col-span-2 text-xs text-[#7c3a20] bg-[#F5E4DD] border border-[#D4A652]/25 rounded px-3 py-2">
                  Cancelada em {detalheAlvo.cancelada_em?.slice(0, 10) || "—"} por {detalheAlvo.cancelada_por || "—"} · Motivo: {detalheAlvo.cancelada_motivo || "—"}
                </div>
              )}
              <div className="col-span-2 text-[11px] text-stone-500 pt-2 border-t border-stone-200">
                Criada em {detalheAlvo.created_at?.slice(0, 10) || "—"} · Atualizada em {detalheAlvo.updated_at?.slice(0, 10) || "—"}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Receber (avulsa) */}
      <Dialog open={!!receberAlvo} onOpenChange={(v) => { if (!v) setReceberAlvo(null); }}>
        <DialogContent className="max-w-md" data-testid="dialog-cr-receber">
          <DialogHeader>
            <DialogTitle>Registrar recebimento</DialogTitle>
            <DialogDescription>
              Somente contas avulsas podem ter recebimento registrado aqui. Não gera pagamento, nem movimento financeiro automaticamente.
            </DialogDescription>
          </DialogHeader>
          {receberAlvo && (
            <div className="text-sm text-stone-700 space-y-1">
              <div className="text-xs text-stone-500 truncate">{receberAlvo.descricao || "—"}</div>
              <div>Valor final: <b className="tabular-nums">{brl(receberAlvo.valor_final)}</b></div>
              <div>Saldo atual: <b className="tabular-nums text-[#D4A652]">{brl(receberAlvo.saldo)}</b></div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cr-rec-valor">Valor recebido *</Label>
              <Input id="cr-rec-valor" data-testid="input-cr-rec-valor" type="number" step="0.01" min="0.01"
                value={receberForm.valor_recebido}
                onChange={(e) => setReceberForm({ ...receberForm, valor_recebido: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="cr-rec-data">Data *</Label>
              <Input id="cr-rec-data" data-testid="input-cr-rec-data" type="date"
                value={receberForm.data_recebimento}
                onChange={(e) => setReceberForm({ ...receberForm, data_recebimento: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label htmlFor="cr-rec-forma">Forma de pagamento</Label>
              <Input id="cr-rec-forma" data-testid="input-cr-rec-forma"
                value={receberForm.forma_pagamento}
                onChange={(e) => setReceberForm({ ...receberForm, forma_pagamento: e.target.value })}
                placeholder="pix, dinheiro, transferência..." />
            </div>
            <div className="col-span-2">
              <Label htmlFor="cr-rec-obs">Observação</Label>
              <Input id="cr-rec-obs" data-testid="input-cr-rec-obs"
                value={receberForm.observacao}
                onChange={(e) => setReceberForm({ ...receberForm, observacao: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceberAlvo(null)} disabled={recebendo}>Cancelar</Button>
            <Button data-testid="btn-confirmar-receber" onClick={executarReceber} disabled={recebendo}>
              {recebendo ? "Registrando..." : "Registrar recebimento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancelar */}
      <Dialog open={!!cancelarAlvo} onOpenChange={(v) => { if (!v) { setCancelarAlvo(null); setCancelarMotivo(""); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-cr-cancelar">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-[#D4A652]" /> Cancelar conta a receber?
            </DialogTitle>
            <DialogDescription>
              A conta será marcada como cancelada. O cancelamento é auditado e não pode ser desfeito por esta tela.
            </DialogDescription>
          </DialogHeader>
          {cancelarAlvo && (
            <div className="text-sm text-stone-700 space-y-1">
              <div className="text-xs text-stone-500 truncate">{cancelarAlvo.descricao || "—"}</div>
              <div>Valor final: <b className="tabular-nums">{brl(cancelarAlvo.valor_final)}</b> · Venc.: {fmtDate(cancelarAlvo.data_vencimento)}</div>
            </div>
          )}
          <div>
            <Label htmlFor="cr-cancel-motivo">Motivo *</Label>
            <Input id="cr-cancel-motivo" data-testid="input-cr-cancel-motivo"
              value={cancelarMotivo} onChange={(e) => setCancelarMotivo(e.target.value)}
              placeholder="Descreva o motivo do cancelamento" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelarAlvo(null); setCancelarMotivo(""); }} disabled={cancelando}>
              Voltar
            </Button>
            <Button data-testid="btn-confirmar-cancelar" onClick={executarCancelar}
              disabled={cancelando || !cancelarMotivo.trim()}>
              {cancelando ? "Cancelando..." : "Confirmar cancelamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
